/**
 * Toast Notification Component
 *
 * A unified toast notification component with support for multiple types, positions,
 * and optional action buttons. Designed to work seamlessly with the useToast hook.
 *
 * @component
 * @example
 * ```tsx
 * // With useToast hook (recommended)
 * const { toast, showToast, hideToast } = useToast();
 *
 * showToast('Item saved!', 'success');
 *
 * <Toast
 *   message={toast.message}
 *   isVisible={toast.isVisible}
 *   type={toast.type}
 *   onClose={hideToast}
 *   action={toast.action}
 *   position={toast.position}
 * />
 *
 * // Standalone usage
 * <Toast
 *   message="File uploaded successfully"
 *   isVisible={true}
 *   type="success"
 *   onClose={handleClose}
 * />
 *
 * // With action button
 * <Toast
 *   message="Item deleted"
 *   isVisible={true}
 *   type="info"
 *   onClose={handleClose}
 *   action={{
 *     label: 'Undo',
 *     onClick: handleUndo
 *   }}
 * />
 * ```
 *
 * Features:
 * - Six toast types with distinct styling: success, error, info, warning, prep (green), return (purple)
 * - Two position options: bottom-right, bottom-center
 * - Optional action buttons
 * - Smooth slide-up animation
 * - Accessible with ARIA labels
 * - Responsive design
 * - Always shows X button for manual dismissal (even with action buttons)
 */

import React from 'react';
import type { ToastType, ToastPosition, ToastAction } from '../hooks/useToast';

export interface ToastProps {
  /** The message to display in the toast */
  message: string;
  /** Whether the toast is visible */
  isVisible: boolean;
  /** Toast type - determines color scheme and icon (default: 'info') */
  type?: ToastType;
  /** Callback when close button is clicked */
  onClose: () => void;
  /** Optional action button with label and click handler */
  action?: ToastAction;
  /** Toast position on screen (default: 'bottom-right') */
  position?: ToastPosition;
  /** Hide the X close button (useful when action button should be the only way to dismiss) */
  hideCloseButton?: boolean;
}

export const Toast: React.FC<ToastProps> = ({
  message,
  isVisible,
  type = 'info',
  onClose,
  action,
  position = 'bottom-right',
  hideCloseButton = false,
}) => {
  if (!isVisible) return null;

  const typeStyles = {
    success: 'bg-success text-ink-inverse',
    error: 'bg-danger text-ink-inverse',
    // Brand cyan, not the blue `info` status token — this toast renders in every
    // department, so it is `primary` (docs/STYLING.md §2).
    info: 'bg-primary text-ink-on-primary',
    warning: 'bg-warning text-ink-inverse',
    // prep/return are rental *categories*, not status meanings (Exception 6).
    prep: 'bg-green-600 text-ink-inverse',
    return: 'bg-purple-600 text-ink-inverse',
  };

  const positionStyles = {
    'bottom-right': 'bottom-4 right-4',
    'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2',
  };

  const animationClass =
    position === 'bottom-center' ? 'animate-slide-up-center' : 'animate-slide-up';

  return (
    <div
      className={`fixed ${positionStyles[position]} z-[9999] ${typeStyles[type]} px-4 py-3 rounded-lg shadow-xl flex items-center gap-3 min-w-[300px] max-w-md ${animationClass}`}
      role="alert"
      aria-live="polite"
    >
      <span className="flex-1">{message}</span>

      {action && (
        <button
          onClick={() => {
            action.onClick();
            onClose();
          }}
          className="px-3 py-1 bg-surface text-ink hover:bg-surface-hover rounded font-medium transition-colors uppercase text-sm tracking-wider"
          aria-label={action.label}
        >
          {action.label}
        </button>
      )}

      {/* Show X button for manual dismissal (unless hideCloseButton is true) */}
      {!hideCloseButton && (
        <button
          onClick={onClose}
          className="text-ink-inverse hover:text-ink-inverse-muted transition-colors ml-2"
          aria-label="Close notification"
        >
          <i className="fa-solid fa-times"></i>
        </button>
      )}
    </div>
  );
};
